import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'sonner'

import type {
    ConfirmOrderPayload,
    CreateOrderRequest,
    ListOrdersParams,
    ListStockOutNotesParams,
    QuickSaleRequest,
    RecordPaymentInput,
    SalesOrder,
    UpdateOrderRequest,
} from '@/lib/api/sales-order-api'

import { m } from '@/lib/paraglide/messages'
import { salesOrderApi, stockOutNoteApi } from '@/lib/api/sales-order-api'
import { tripKeys } from '@/hooks/use-delivery-trips-query'
import { vehicleKeys } from '@/hooks/use-vehicles-query'
import { formatApiError } from '@/lib/format-api-error'
import { orderPollInterval } from '@/lib/order-poll'

// Pipe an error through formatApiError so the toast carries the server's
// real reason + the X-Request-ID trace tag — turns a generic "thất bại"
// into something a user can quote and a dev can grep.
const errorToast = (fallback: string) => (err: unknown) => {
    const payload = formatApiError(err, fallback)
    toast.error(payload.title, { description: payload.description })
}

export const orderKeys = {
    all: ['sales-orders'] as const,
    lists: () => [...orderKeys.all, 'list'] as const,
    list: (params: ListOrdersParams) => [...orderKeys.lists(), params] as const,
    detail: (id: string) => [...orderKeys.all, 'detail', id] as const,
    timeline: (id: string) => [...orderKeys.all, 'timeline', id] as const,
    payments: (id: string) => [...orderKeys.all, 'payments', id] as const,
    paymentQR: (id: string) => [...orderKeys.all, id, 'payment-qr'] as const,
    packagingDraft: (id: string) =>
        [...orderKeys.all, 'packaging-draft', id] as const,
    packagingDeducted: (id: string) =>
        [...orderKeys.all, 'packaging-deducted', id] as const,
    inventoryDeducted: (id: string) =>
        [...orderKeys.all, 'inventory-deducted', id] as const,
    stockCheck: (id: string) => [...orderKeys.all, 'stock-check', id] as const,
    assignableDrivers: () => [...orderKeys.all, 'assignable-drivers'] as const,
}

/**
 * Tài xế cho popup gán đơn. Chỉ fetch khi popup mở (`enabled`) để không gọi
 * endpoint cần quyền `sales_orders.assign_driver` với mọi lượt xem đơn.
 */
export function useAssignableDrivers(enabled: boolean) {
    return useQuery({
        queryKey: orderKeys.assignableDrivers(),
        queryFn: async () => {
            const res = await salesOrderApi.assignableDrivers()
            return res.data ?? []
        },
        enabled,
    })
}

export function useSalesOrders(params?: ListOrdersParams) {
    return useQuery({
        queryKey: orderKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await salesOrderApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

const INFINITE_ORDER_PAGE_SIZE = 20

interface InfiniteOrdersPage {
    items: SalesOrder[]
    meta:
        | { page: number; per_page: number; total: number; total_pages: number }
        | undefined
}

/**
 * Ô chọn đơn có debounce + cuộn tải thêm (màn đối soát). Chỉ lấy đơn còn nhận
 * thanh toán (payable=true: chưa huỷ + còn phải thu > 0). Dùng chung API danh
 * sách đơn, search theo mã đơn / mã nội bộ / tên khách / SĐT; mới nhất lên trên.
 */
export function useInfiniteSalesOrdersQuery(search: string) {
    return useInfiniteQuery({
        queryKey: [...orderKeys.lists(), 'infinite-payable', search] as const,
        queryFn: async ({ pageParam = 1 }): Promise<InfiniteOrdersPage> => {
            const res = await salesOrderApi.list({
                page: pageParam,
                per_page: INFINITE_ORDER_PAGE_SIZE,
                search: search || undefined,
                payable: true,
            })
            return { items: res.data ?? [], meta: res.meta }
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
            lastPage.meta && lastPage.meta.page < lastPage.meta.total_pages
                ? lastPage.meta.page + 1
                : undefined,
    })
}

export function useSalesOrder(
    id: string,
    opts?: { pollWhileUnpaid?: boolean },
) {
    return useQuery({
        queryKey: orderKeys.detail(id),
        queryFn: async () => {
            const res = await salesOrderApi.getById(id)
            return res.data ?? null
        },
        enabled: Boolean(id),
        // While a bank-transfer invoice popup waits for the SePay webhook, poll
        // the order every 4s until it flips to paid (no SSE on /sale screens yet).
        refetchInterval: (query) =>
            orderPollInterval(
                opts?.pollWhileUnpaid ?? false,
                query.state.data?.payment_status,
            ),
    })
}

export function useOrderTimeline(id: string) {
    return useQuery({
        queryKey: orderKeys.timeline(id),
        queryFn: async () => {
            const res = await salesOrderApi.timeline(id)
            return res.data ?? []
        },
        enabled: Boolean(id),
    })
}

// Phase 4 — đọc draft packaging của đơn (sale chốt lúc tạo). FE Confirm flow
// dùng để hiển thị + override packaging_items.
export function useOrderPackagingDraft(id: string, enabled = true) {
    return useQuery({
        queryKey: orderKeys.packagingDraft(id),
        queryFn: async () => {
            const res = await salesOrderApi.getPackagingDraft(id)
            return res.data ?? []
        },
        enabled: Boolean(id) && enabled,
        staleTime: 0, // mỗi lần Confirm cần fresh
    })
}

// Vật tư đã trừ ở Confirm step (group theo lot). Dùng cho:
//  - ConfirmReturn popup prefill list.
//  - Trang chi tiết đơn hiển thị "Vật tư đã trừ".
export function useOrderPackagingDeducted(id: string, enabled = true) {
    return useQuery({
        queryKey: orderKeys.packagingDeducted(id),
        queryFn: async () => {
            const res = await salesOrderApi.getPackagingDeducted(id)
            return res.data ?? []
        },
        enabled: Boolean(id) && enabled,
        staleTime: 0,
    })
}

// SP đã trừ kho ở Confirm step (group theo lot). Mirror packagingDeducted —
// dùng cho ConfirmReturn popup prefill phần SP.
export function useOrderInventoryDeducted(id: string, enabled = true) {
    return useQuery({
        queryKey: orderKeys.inventoryDeducted(id),
        queryFn: async () => {
            const res = await salesOrderApi.getInventoryDeducted(id)
            return res.data ?? []
        },
        enabled: Boolean(id) && enabled,
        staleTime: 0,
    })
}

export function useOrderPayments(id: string) {
    return useQuery({
        queryKey: orderKeys.payments(id),
        queryFn: async () => {
            const res = await salesOrderApi.listPayments(id)
            return res.data ?? []
        },
        enabled: Boolean(id),
    })
}

export function usePaymentQR(orderId: string, enabled: boolean) {
    return useQuery({
        queryKey: orderKeys.paymentQR(orderId),
        queryFn: async () => {
            const res = await salesOrderApi.getPaymentQR(orderId)
            return res.data ?? null
        },
        enabled: enabled && Boolean(orderId),
        refetchInterval: (query) => {
            // Stop polling once payment confirmed (amount === 0 means fully paid)
            const data = query.state.data
            if (data && data.amount <= 0) return false
            return 3000
        },
    })
}

// useNotifications moved to use-notifications.ts (single source of truth)

/**
 * Kiểm tra tồn kho đủ để Confirm cho đơn AwaitingPackaging.
 * Enable=false khi order không ở trạng thái awaiting_packaging (BE sẽ 400).
 * FE poll nhẹ để cashier F5 thấy kho đủ mà bấm Confirm.
 */
export function useStockCheck(orderId: string, enabled: boolean) {
    return useQuery({
        queryKey: orderKeys.stockCheck(orderId),
        queryFn: async () => {
            const res = await salesOrderApi.checkStock(orderId)
            return res.data ?? { ready: false, shortfalls: [] }
        },
        enabled: enabled && Boolean(orderId),
        // Không auto-poll để tránh spam BE; cashier bấm "Kiểm tra đủ hàng"
        // để refetch tay khi cần.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
    })
}

export function useCreateOrder(options?: {
    // Return true để bỏ qua default toast (caller sẽ hiển thị UI riêng, vd
    // popup 409 INSUFFICIENT_STOCK cho luồng AwaitingPackaging).
    onErrorHandled?: (err: unknown) => boolean
}) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateOrderRequest) => salesOrderApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_create_success())
        },
        onError: (err) => {
            const handled = options?.onErrorHandled?.(err) ?? false
            if (!handled) {
                errorToast(m.toast_order_create_error())(err)
            }
        },
    })
}

export function useUpdateOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateOrderRequest }) =>
            salesOrderApi.update(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_update_success())
        },
        onError: errorToast(m.toast_order_update_error()),
    })
}

export function useUpdateInternalOrderCode() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, code }: { id: string; code: string }) =>
            salesOrderApi.updateInternalCode(id, code),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_internal_code_success())
        },
        onError: errorToast(m.toast_order_update_error()),
    })
}

export function useQuickCreateOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: QuickSaleRequest) => salesOrderApi.quickCreate(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_quick_success())
        },
        onError: errorToast(m.toast_order_quick_error()),
    })
}

function useSimpleAction(
    apiFn: (id: string) => Promise<unknown>,
    successMsg: string,
    errorMsg: string,
) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id }: { id: string }) => apiFn(id),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            toast.success(successMsg)
        },
        onError: errorToast(errorMsg),
    })
}

function useNoteAction(
    apiFn: (id: string, data: { note: string }) => Promise<unknown>,
    successMsg: string,
    errorMsg: string,
) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: { note: string } }) =>
            apiFn(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            toast.success(successMsg)
        },
        onError: errorToast(errorMsg),
    })
}

export function useConfirmOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data?: ConfirmOrderPayload
        }) => salesOrderApi.confirm(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            // Lot balances changed → drop any cached available-lots list.
            void qc.invalidateQueries({ queryKey: ['inventory-lots'] })
            toast.success(m.toast_order_confirm_success())
        },
        onError: errorToast(m.toast_order_confirm_error()),
    })
}

export function useStockOutOrder() {
    return useSimpleAction(
        (id) => salesOrderApi.stockOut(id),
        m.toast_order_stock_out_success(),
        m.toast_order_stock_out_error(),
    )
}

export function useStartDelivery() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, vehicle_id }: { id: string; vehicle_id?: string }) =>
            salesOrderApi.startDelivery(id, { vehicle_id }),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            void qc.invalidateQueries({ queryKey: tripKeys.active() })
            void qc.invalidateQueries({ queryKey: vehicleKeys.available() })
            toast.success(m.toast_order_start_delivery_success())
        },
        onError: (err) => {
            const code =
                err instanceof AxiosError
                    ? err.response?.data?.error?.code
                    : undefined
            if (code === 'VEHICLE_NOT_AVAILABLE') {
                toast.error(m.trip_error_vehicle_not_available())
            } else if (code === 'VEHICLE_REQUIRED') {
                toast.error(m.trip_error_vehicle_required())
            } else if (code === 'TRIP_VEHICLE_MISMATCH') {
                toast.error(m.trip_error_vehicle_mismatch())
            } else {
                errorToast(m.toast_order_start_delivery_error())(err)
            }
        },
    })
}

export function useConfirmDelivery() {
    const qc = useQueryClient()
    return useMutation({
        // Forward the FULL settlement payload — dropping method/cash_amount
        // here silently turns a driver cash collection into "record nothing".
        mutationFn: ({
            id,
            ...data
        }: { id: string } & Parameters<
            typeof salesOrderApi.confirmDelivery
        >[1]) => salesOrderApi.confirmDelivery(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            toast.success(m.toast_order_confirm_delivery_success())
        },
        onError: errorToast(m.toast_order_confirm_delivery_error()),
    })
}

export function useConfirmReturn() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data?: {
                note?: string
                inventory_restore_items?: {
                    inventory_lot_id: string
                    quantity: number
                }[]
                packaging_restore_items?: {
                    packaging_lot_id: string
                    quantity: number
                }[]
            }
        }) => salesOrderApi.confirmReturn(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            void qc.invalidateQueries({
                queryKey: orderKeys.packagingDeducted(id),
            })
            toast.success(m.toast_order_return_success())
        },
        onError: errorToast(m.toast_order_return_error()),
    })
}

export function useAssignDriver() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: { driver_id: string }
        }) => salesOrderApi.assignDriver(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_assign_driver_success())
        },
        onError: errorToast(m.toast_order_assign_driver_error()),
    })
}

export function usePickupOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data?: { note?: string } }) =>
            salesOrderApi.pickupOrder(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            toast.success(m.toast_order_pickup_success())
        },
        onError: errorToast(m.toast_order_pickup_error()),
    })
}

export function useFailDelivery() {
    return useNoteAction(
        (id, data) => salesOrderApi.failDelivery(id, data),
        m.toast_order_fail_delivery_success(),
        m.toast_order_fail_delivery_error(),
    )
}

// Carrier reconcile hooks — kế toán mark trạng thái cho đơn của carrier
// ngoài (Viettelpost, GHTK, …). Note optional. Cùng invalidate + toast
// shape với các delivery hook khác.
function useCarrierMarkAction(
    apiFn: (id: string, data?: { note?: string }) => Promise<unknown>,
    successMsg: string,
    errorMsg: string,
) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data?: { note?: string } }) =>
            apiFn(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            toast.success(successMsg)
        },
        onError: errorToast(errorMsg),
    })
}

export function useCarrierMarkReadyToDeliver() {
    return useCarrierMarkAction(
        (id, data) => salesOrderApi.carrierMarkReadyToDeliver(id, data),
        m.toast_order_carrier_mark_ready_to_deliver_success(),
        m.toast_order_carrier_mark_error(),
    )
}

export function useCarrierMarkInDelivery() {
    return useCarrierMarkAction(
        (id, data) => salesOrderApi.carrierMarkInDelivery(id, data),
        m.toast_order_carrier_mark_in_delivery_success(),
        m.toast_order_carrier_mark_error(),
    )
}

export function useCarrierMarkDelivered() {
    return useCarrierMarkAction(
        (id, data) => salesOrderApi.carrierMarkDelivered(id, data),
        m.toast_order_carrier_mark_delivered_success(),
        m.toast_order_carrier_mark_error(),
    )
}

export function useCarrierMarkDeliveryFailed() {
    return useCarrierMarkAction(
        (id, data) => salesOrderApi.carrierMarkDeliveryFailed(id, data),
        m.toast_order_carrier_mark_delivery_failed_success(),
        m.toast_order_carrier_mark_error(),
    )
}

export function useCarrierMarkReturned() {
    return useCarrierMarkAction(
        (id, data) => salesOrderApi.carrierMarkReturned(id, data),
        m.toast_order_carrier_mark_returned_success(),
        m.toast_order_carrier_mark_error(),
    )
}

export function useCancelOrder() {
    return useNoteAction(
        (id, data) => salesOrderApi.cancel(id, data),
        m.toast_order_cancel_success(),
        m.toast_order_cancel_error(),
    )
}

export function useRecordOrderPayments(orderId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (payments: RecordPaymentInput[]) =>
            salesOrderApi.recordPayments(orderId, { payments }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) })
            void qc.invalidateQueries({ queryKey: orderKeys.payments(orderId) })
            toast.success(m.toast_order_payment_success())
        },
        onError: errorToast(m.toast_order_payment_error()),
    })
}

export const stockOutNoteKeys = {
    all: ['stock-out-notes'] as const,
    byCode: (code: string) =>
        [...stockOutNoteKeys.all, 'by-code', code] as const,
    byOrder: (orderId: string) =>
        [...stockOutNoteKeys.all, 'by-order', orderId] as const,
    list: (params: ListStockOutNotesParams) =>
        [...stockOutNoteKeys.all, 'list', params] as const,
}

export function useStockOutNote(code: string) {
    return useQuery({
        queryKey: stockOutNoteKeys.byCode(code),
        queryFn: async () => {
            const res = await stockOutNoteApi.getByCode(code)
            return res.data ?? null
        },
        enabled: Boolean(code),
        retry: false,
    })
}

/** Fetch a paginated list of stock-out notes with optional status filter. */
export function useStockOutNotes(params?: ListStockOutNotesParams) {
    return useQuery({
        queryKey: stockOutNoteKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await stockOutNoteApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

export function useConfirmStockOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            note,
            photoPaths,
        }: {
            id: string
            note?: string
            photoPaths: string[]
        }) =>
            stockOutNoteApi.confirm(id, {
                ...(note ? { note } : {}),
                photo_paths: photoPaths,
            }),
        onSuccess: (res) => {
            void qc.invalidateQueries({ queryKey: stockOutNoteKeys.all })
            const orderId = res.data?.sales_order_id
            if (orderId) {
                void qc.invalidateQueries({
                    queryKey: orderKeys.detail(orderId),
                })
                void qc.invalidateQueries({
                    queryKey: orderKeys.timeline(orderId),
                })
            }
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_stock_out_note_confirm_success())
        },
        onError: errorToast(m.toast_stock_out_note_confirm_error()),
    })
}
